import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { UserNetwork as UserNetworkType, Post } from '../types';
import { Search, Users, MessageCircle, Repeat, Hash, ExternalLink } from 'lucide-react';
import { formatSafeRelativeTime, formatAbsoluteTime } from '../utils/date-formatter';

export function UserNetwork() {
  const { username: urlUsername } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchUsername, setSearchUsername] = useState(urlUsername || '');
  const [networkData, setNetworkData] = useState<UserNetworkType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (urlUsername) {
      loadUserNetwork(urlUsername);
    }
  }, [urlUsername]);

  const loadUserNetwork = async (username: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUserNetwork(username);
      setNetworkData(data);
    } catch (error) {
      console.error('Failed to load user network:', error);
      setError('Failed to load user network data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchUsername.trim()) {
      navigate(`/network/${searchUsername.trim()}`);
    }
  };

  const getRelationshipIcon = (type: string) => {
    switch (type) {
      case 'mention': return <MessageCircle className="h-4 w-4" />;
      case 'retweet': return <Repeat className="h-4 w-4" />;
      case 'reply': return <MessageCircle className="h-4 w-4" />;
      default: return <Hash className="h-4 w-4" />;
    }
  };

  const getThreatBadgeClass = (level: string) => {
    const baseClass = 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ';
    switch (level) {
      case 'critical': return baseClass + 'bg-red-100 text-red-800';
      case 'high': return baseClass + 'bg-orange-100 text-orange-800';
      case 'medium': return baseClass + 'bg-yellow-100 text-yellow-800';
      case 'low': return baseClass + 'bg-green-100 text-green-800';
      default: return baseClass + 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900">User Network Analysis</h2>
        
        <div className="flex items-center space-x-2">
          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Enter username..."
              value={searchUsername}
              onChange={(e) => setSearchUsername(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Search
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {networkData && !loading && (
        <>
          {/* User Overview */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">@{networkData.username}</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>{networkData.posts.length} posts</span>
                  <span>{networkData.relationships.length} connections</span>
                </div>
              </div>
            </div>
          </div>

          {/* Network Relationships */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Network Relationships</h3>
            </div>
            <div className="p-6">
              {networkData.relationships.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No relationships found</p>
              ) : (
                <div className="space-y-4">
                  {networkData.relationships.slice(0, 20).map((rel, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {getRelationshipIcon(rel.relationship_type)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {rel.source_user === networkData.username ? (
                              <>@{networkData.username} → @{rel.target_user}</>
                            ) : (
                              <>@{rel.source_user} → @{networkData.username}</>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            {rel.relationship_type} • {rel.mention_count} interactions
                          </p>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        Last: {new Date(rel.last_seen).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                  
                  {networkData.relationships.length > 20 && (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">
                        Showing 20 of {networkData.relationships.length} relationships
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* User Posts */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Posts</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {networkData.posts.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  No posts found
                </div>
              ) : (
                networkData.posts.map((post) => (
                  <div key={post.id} className="p-6">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <p className="text-sm font-medium text-gray-900">
                            @{post.username}
                          </p>
                          <span className={getThreatBadgeClass(post.threat_level)}>
                            {post.threat_level.toUpperCase()}
                          </span>
                          <div className="flex items-center text-sm text-gray-500">
                            <span>Relevance: {(post.relevance_score * 100).toFixed(0)}%</span>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-gray-900 text-sm leading-relaxed">
                            {post.content}
                          </p>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{formatSafeRelativeTime(post.created_at, { addSuffix: true })}</span>
                            <span>Feed: {post.source_feed}</span>
                          </div>
                          
                          {post.url && (
                            <a
                              href={post.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View Original
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Network Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MessageCircle className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Mentions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {networkData.relationships.filter(r => r.relationship_type === 'mention').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Repeat className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Retweets</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {networkData.relationships.filter(r => r.relationship_type === 'retweet').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Hash className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Replies</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {networkData.relationships.filter(r => r.relationship_type === 'reply').length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}